import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Play, LogIn, Users, Globe, UserPlus, X, LogOut, Smartphone, History, ShieldCheck, ChevronDown, UserRound, Settings as SettingsIcon } from "lucide-react";
import { COUNTRIES } from "@/data/countries";
import { saveUser } from "@/lib/session";
import { detectCountryCode } from "@/lib/detectCountry";
import { useAuth } from "@/lib/auth";
import AuthModal from "@/components/AuthModal";
import FriendsPanel from "@/components/FriendsPanel";
import ServiceGrid from "@/components/ServiceGrid";
import DiscoveryFeed from "@/components/DiscoveryFeed";
import AdminPanel from "@/components/AdminPanel";
import { BrandLogo } from "@/components/BrandLogo";
import CyberBackground from "@/components/CyberBackground";
import CountryPicker from "@/components/CountryPicker";
import LegalFooter from "@/components/LegalFooter";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Home() {
  const navigate = useNavigate();
  const { account, logout, incoming } = useAuth();
  const [name, setName] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [friendsOpen, setFriendsOpen] = useState(false);
  const [friendsTab, setFriendsTab] = useState("friends");
  const [adminOpen, setAdminOpen] = useState(false);
  const [accountMenu, setAccountMenu] = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, [account]);

  // prefill identity: logged-in account's country, else auto-detect from locale
  useEffect(() => {
    if (account) {
      setName((n) => n || account.username);
      if (account.countryCode) setCountryCode(account.countryCode);
    } else {
      setCountryCode((c) => c || detectCountryCode() || "");
    }
  }, [account]);

  const buildUser = () => {
    if (!name.trim()) { toast.error("Please enter your name"); return null; }
    if (!countryCode) { toast.error("Please select your country"); return null; }
    const c = COUNTRIES.find((x) => x.code === countryCode);
    return {
      name: name.trim(), country: c.name, flag: c.flag, language: c.language, countryCode: c.code,
      ...(account ? { accountId: account.id, avatarColor: account.avatarColor, avatarEmoji: account.avatarEmoji } : {}),
    };
  };

  const handleCreate = async (tvMode = false) => {
    const user = buildUser();
    if (!user) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/rooms`);
      saveUser(user);
      navigate(`/room/${res.data.code}${tvMode ? "?tv=1" : ""}`);
    } catch (e) {
      toast.error("Could not create room. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const launchRoom = async (params = "") => {
    const user = buildUser();
    if (!user) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/rooms`);
      saveUser(user);
      navigate(`/room/${res.data.code}${params}`);
    } catch (e) {
      toast.error("Could not create room. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    const user = buildUser();
    if (!user) return;
    const code = joinCode.trim().toUpperCase();
    if (code.length !== 6) { toast.error("Room code must be 6 letters"); return; }
    setLoading(true);
    try {
      await axios.get(`${API}/rooms/${code}`);
      saveUser(user);
      navigate(`/room/${code}`);
    } catch (e) {
      toast.error("Room not found. Check the code.");
    } finally {
      setLoading(false);
    }
  };

  const rewatch = async (url) => {
    if (!account) return;
    try {
      const res = await axios.post(`${API}/rooms`);
      saveUser({
        name: account.username, country: account.country, flag: account.flag,
        language: account.language, countryCode: account.countryCode,
        accountId: account.id, avatarColor: account.avatarColor, avatarEmoji: account.avatarEmoji,
      });
      setFriendsOpen(false);
      navigate(`/room/${res.data.code}?v=${encodeURIComponent(url)}`);
    } catch (e) {
      toast.error("Could not start the room. Try again.");
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-[#0A0E27] px-4 py-10">
      <CyberBackground />

      {/* Top bar: logged-in nav (history / friends / account) or log-in for visitors */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-end gap-2 px-4 sm:px-6 py-4">
        {account ? (
          <>
            {account.isAdmin && (
              <button
                data-testid="open-admin-button"
                onClick={() => setAdminOpen(true)}
                className="btn-neon flex items-center gap-2 h-10 px-4 rounded-full glass-cyber text-sm font-semibold text-[#FFC107] hover:text-[#FFD84A] glow-cyan"
                title="Admin dashboard"
              >
                <ShieldCheck className="w-4 h-4" /> <span className="hidden sm:inline">Admin</span>
              </button>
            )}
            <button
              data-testid="open-history-button"
              onClick={() => { setFriendsTab("history"); setFriendsOpen(true); }}
              className="btn-neon flex items-center gap-2 h-10 px-4 rounded-full glass-cyber text-sm font-semibold text-[#F0F6FF] hover:text-[#00D9FF] glow-cyan"
              title="Your watch history"
            >
              <History className="w-4 h-4" /> <span className="hidden sm:inline">History</span>
            </button>
            <button
              data-testid="open-friends-button"
              onClick={() => setFriendsOpen(true)}
              className="btn-neon relative flex items-center gap-2 h-10 px-4 rounded-full glass-cyber text-sm font-semibold text-[#F0F6FF] hover:text-[#00D9FF] glow-cyan"
            >
              <UserPlus className="w-4 h-4" /> Friends
              {incoming.length > 0 && (
                <span data-testid="friends-badge" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#FF006E] text-white text-[10px] font-bold flex items-center justify-center glow-pink">{incoming.length}</span>
              )}
            </button>
            <div className="relative">
              <button
                data-testid="account-chip"
                onClick={() => setAccountMenu((v) => !v)}
                className="btn-neon flex items-center gap-2 h-10 pl-1.5 pr-2.5 rounded-full glass-cyber hover:border-[#00D9FF]/50"
              >
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-sm" style={{ backgroundColor: account.avatarColor }}>{account.avatarEmoji}</div>
                <span className="text-sm font-semibold text-[#F0F6FF] hidden sm:inline">@{account.username}</span>
                <ChevronDown className={`w-4 h-4 text-[#88A0C0] transition-transform ${accountMenu ? "rotate-180" : ""}`} />
              </button>
              {accountMenu && (
                <>
                  <div className="fixed inset-0 z-[54]" onClick={() => setAccountMenu(false)} />
                  <div data-testid="account-menu" className="absolute right-0 mt-2 w-52 z-[56] glass-cyber rounded-2xl p-1.5 shadow-2xl anim-in">
                    <button data-testid="menu-profile" onClick={() => { setAccountMenu(false); setFriendsTab("friends"); setFriendsOpen(true); }} className="w-full flex items-center gap-2.5 h-10 px-3 rounded-xl text-sm text-[#F0F6FF] hover:bg-white/8 transition-colors">
                      <UserRound className="w-4 h-4 text-[#00D9FF]" /> Profile
                    </button>
                    <button data-testid="menu-history" onClick={() => { setAccountMenu(false); setFriendsTab("history"); setFriendsOpen(true); }} className="w-full flex items-center gap-2.5 h-10 px-3 rounded-xl text-sm text-[#F0F6FF] hover:bg-white/8 transition-colors">
                      <History className="w-4 h-4 text-[#9D4EDD]" /> History
                    </button>
                    {account.isAdmin && (
                      <button data-testid="menu-admin" onClick={() => { setAccountMenu(false); setAdminOpen(true); }} className="w-full flex items-center gap-2.5 h-10 px-3 rounded-xl text-sm text-[#FFC107] hover:bg-white/8 transition-colors">
                        <ShieldCheck className="w-4 h-4" /> Admin
                      </button>
                    )}
                    <div className="h-px bg-white/8 my-1" />
                    <button data-testid="logout-button" onClick={() => { setAccountMenu(false); setLogoutConfirm(true); }} className="w-full flex items-center gap-2.5 h-10 px-3 rounded-xl text-sm text-[#FF3B5C] hover:bg-[#FF0040]/10 transition-colors">
                      <LogOut className="w-4 h-4" /> Log out
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <button
            data-testid="open-auth-button"
            onClick={() => setAuthOpen(true)}
            className="btn-neon flex items-center gap-2 h-10 px-5 rounded-full glass-cyber text-sm font-semibold text-[#F0F6FF] hover:text-[#00D9FF] glow-cyan"
          >
            <LogIn className="w-4 h-4" /> Log in / Sign up
          </button>
        )}
      </div>

      <div className="relative z-10 w-full max-w-6xl grid grid-cols-1 lg:grid-cols-[minmax(0,40%)_minmax(0,60%)] gap-8 lg:gap-12 lg:items-start">
        {/* LEFT — create / join actions */}
        <div className="w-full">
        <div className="mb-8 flex flex-col items-start gap-3 anim-in">
          <BrandLogo className="text-5xl md:text-7xl" />
          <p className="text-[#88A0C0] text-base md:text-lg max-w-md leading-relaxed">
            Watch YouTube together, in perfect sync. Chat across languages with live AI translation and your own AI party host.
          </p>
          {stats && (
            <div data-testid="visitor-counter" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-mono-tech">
              <span className="uppercase tracking-wider">
                <span className="neon-cyan font-bold text-base">{stats.totalUsers.toLocaleString()}</span>
                <span className="text-[#88A0C0]"> MEMBERS</span>
              </span>
              {stats.online > 0 && (
                <span className="flex items-center gap-1.5 uppercase tracking-wider">
                  <span className="flex h-2 w-2 relative">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FF66] opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00FF66]" style={{ boxShadow: "0 0 8px #00FF66" }} />
                  </span>
                  <span style={{ color: "#00FF66", textShadow: "0 0 8px rgba(0,255,102,0.7)" }} className="font-bold">{stats.online} ONLINE</span>
                </span>
              )}
            </div>
          )}
          {stats && stats.installs > 0 && (
            <div data-testid="install-counter" className="flex items-center gap-1.5 text-xs font-mono-tech uppercase tracking-wider">
              <Smartphone className="w-3.5 h-3.5 text-[#FF006E]" />
              <span className="neon-pink font-bold">{stats.installs.toLocaleString()}</span>
              <span className="text-[#88A0C0]">added to home screen</span>
            </div>
          )}
        </div>

        <div
          data-testid="home-card"
          className="rounded-2xl p-7 md:p-9 glass-cyber anim-in"
          style={{ animationDelay: "0.1s" }}
        >
          <div className="space-y-5">
            <div>
              <label className="text-xs font-bold neon-cyan uppercase tracking-widest mb-2 flex items-center gap-2 font-display">
                <Users className="w-3.5 h-3.5" /> Your name
              </label>
              <Input
                data-testid="name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Alex"
                className="input-neon h-12 bg-[#060817]/80 border-[#00D9FF]/25 text-[#F0F6FF] rounded-xl text-base"
              />
            </div>

            <div>
              <label className="text-xs font-bold neon-cyan uppercase tracking-widest mb-2 flex items-center gap-2 font-display">
                <Globe className="w-3.5 h-3.5" /> Country
              </label>
              <CountryPicker
                value={countryCode}
                onChange={setCountryCode}
                triggerTestId="country-select"
                optionPrefix="country-option"
                searchTestId="country-search"
                placeholder="Select your country"
              />
            </div>

            <button
              data-testid="create-room-button"
              onClick={() => handleCreate(false)}
              disabled={loading}
              className="btn-neon glow-cyan w-full h-12 bg-[#00D9FF]/12 border border-[#00D9FF] text-[#00D9FF] font-display font-bold uppercase tracking-widest rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Play className="w-5 h-5 fill-[#00D9FF]" /> Create a Room
            </button>

            <button
              data-testid="livetv-home-button"
              onClick={() => handleCreate(true)}
              disabled={loading}
              className="btn-neon glow-pink w-full h-12 bg-[#FF006E]/12 border border-[#FF006E] text-[#FF006E] font-display font-bold uppercase tracking-widest rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
            >
              📡 Live TV
            </button>

            <div className="flex items-center gap-3 py-1">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#9D4EDD]/50" />
              <span className="text-xs neon-purple uppercase tracking-widest font-display">or join</span>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#9D4EDD]/50" />
            </div>

            <div className="space-y-2.5">
              <input
                data-testid="room-code-input"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
                onKeyDown={(e) => { if (e.key === "Enter" && joinCode.length === 6) handleJoin(); }}
                placeholder="ROOM CODE"
                autoCapitalize="characters"
                autoComplete="off"
                maxLength={6}
                className={`w-full h-14 bg-[#060817]/80 border-2 rounded-2xl text-center font-mono-tech font-bold text-2xl text-white uppercase tracking-[0.4em] outline-none transition-all placeholder:text-[#4a5578] placeholder:text-base placeholder:tracking-[0.25em] ${
                  joinCode.length === 6
                    ? "border-[#00FF66] shadow-[0_0_24px_rgba(0,255,102,0.6)]"
                    : joinCode.length > 0
                      ? "border-[#00D9FF] shadow-[0_0_18px_rgba(0,217,255,0.5)]"
                      : "border-[#9D4EDD]/40"
                }`}
              />
              <div className="flex items-center justify-center h-4 text-xs font-mono-tech" data-testid="room-code-counter">
                {joinCode.length === 6 ? (
                  <span className="neon-green font-bold">✅ Code valid</span>
                ) : (
                  <span className="text-[#88A0C0]">{joinCode.length}/6 characters</span>
                )}
              </div>
              <button
                data-testid="join-room-button"
                onClick={handleJoin}
                disabled={loading || joinCode.length !== 6}
                className={`btn-neon w-full h-12 font-display font-bold uppercase tracking-widest rounded-full flex items-center justify-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                  joinCode.length === 6
                    ? "bg-[#00FF66] text-navy-ink glow-green"
                    : "bg-[#9D4EDD]/15 border border-[#9D4EDD] text-[#9D4EDD]"
                }`}
              >
                Join Room →
              </button>
            </div>
          </div>
        </div>
        <p className="text-xs text-[#88A0C0] mt-6 uppercase tracking-wider font-mono-tech text-center lg:text-left">
          {account ? "Your friends get a one-tap invite when you start a party." : "No account needed — just pick a name and press play."}
        </p>
        </div>

        {/* RIGHT — what's playing now + full app */}
        <div className="w-full">
          <DiscoveryFeed loggedOut={!account} />
          {account && (
            <ServiceGrid
              account={account}
              onLaunch={launchRoom}
              onOpenAuth={() => setAuthOpen(true)}
              onOpenFriends={(t) => { setFriendsTab(t); setFriendsOpen(true); }}
            />
          )}
        </div>
      </div>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <AdminPanel open={adminOpen} onClose={() => setAdminOpen(false)} />

      {logoutConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="logout-confirm">
          <div className="absolute inset-0 bg-[#04060F]/80 backdrop-blur-md" onClick={() => setLogoutConfirm(false)} />
          <div className="relative w-full max-w-sm glass-cyber rounded-3xl p-6 anim-in text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#FF0040]/12 border border-[#FF0040]/40 flex items-center justify-center mb-3">
              <LogOut className="w-5 h-5 text-[#FF3B5C]" />
            </div>
            <h3 className="text-lg font-display font-bold text-[#F0F6FF]">Log out?</h3>
            <p className="text-sm text-[#88A0C0] mt-1.5">You'll need to sign in again to watch with your account.</p>
            <div className="flex gap-2.5 mt-5">
              <button data-testid="logout-cancel" onClick={() => setLogoutConfirm(false)} className="btn-neon flex-1 h-11 rounded-full bg-white/5 border border-white/15 text-[#F0F6FF] font-semibold hover:bg-white/10">Cancel</button>
              <button data-testid="logout-confirm-button" onClick={() => { setLogoutConfirm(false); logout(); toast("Logged out"); }} className="btn-neon glow-red flex-1 h-11 rounded-full bg-[#FF0040] text-white font-display font-bold uppercase tracking-wider">Log out</button>
            </div>
          </div>
        </div>
      )}

      {/* Friends drawer */}
      {friendsOpen && (
        <div className="fixed inset-0 z-[55]" data-testid="friends-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setFriendsOpen(false)} />
          <div className="absolute top-0 right-0 h-full w-full max-w-sm glass-cyber border-l border-[#00D9FF]/25 shadow-2xl p-5 overflow-y-auto animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-display font-bold uppercase tracking-wider neon-cyan">Friends</h2>
              <button data-testid="close-friends-button" onClick={() => setFriendsOpen(false)} className="text-[#88A0C0] hover:text-[#00D9FF]"><X className="w-5 h-5" /></button>
            </div>
            <FriendsPanel key={friendsTab} initialTab={friendsTab} onRewatch={rewatch} />
          </div>
        </div>
      )}

      <LegalFooter language={account?.language || COUNTRIES.find((c) => c.code === countryCode)?.language || "English"} />
    </div>
  );
}
